import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { ResourcePool } from "../types/castle";

export interface BattlePassReward {
  resources: ResourcePool;
  inventoryItems?: Record<string, number> | null;
  soulShards: number;
  heroXp: number;
  vipDurationDays: number;
  universalShards?: Record<string, number> | null;
}

export interface BattlePassDay {
  day: number;
  reward: BattlePassReward;
}

export interface BattlePassDef {
  id: string;
  title: string;
  description: string;
  starsPrice: number;
  durationDays: number;
  highlight?: string | null;
  schedule: BattlePassDay[];
}

export interface ActiveBattlePass {
  rowId: number;
  passId: string;
  purchasedAt: string;
  expiresAt: string;
  claimedDaysMask: number;
  currentDay: number;
  secondsRemaining: number;
}

export function useBattlePassCatalog() {
  return useQuery({
    queryKey: ["battle-pass", "catalog"],
    queryFn: async () => {
      const res = await api.get<BattlePassDef[]>("/battle-pass/catalog");
      return res.data;
    },
    staleTime: 60_000,
  });
}

export function useMyBattlePasses() {
  return useQuery({
    queryKey: ["battle-pass", "my"],
    queryFn: async () => {
      const res = await api.get<ActiveBattlePass[]>("/battle-pass/my");
      return res.data;
    },
    // Short staleTime so countdown / claimable state updates as the player
    // sits on the screen across midnight boundaries.
    staleTime: 30_000,
  });
}

export interface ClaimResponse {
  ok: boolean;
  day: number;
  reward: BattlePassReward;
}

export function useClaimBattlePassDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { passId: string; day: number }) => {
      const res = await api.post<ClaimResponse>(
        `/battle-pass/${args.passId}/claim?day=${args.day}`,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["battle-pass", "my"] });
      qc.invalidateQueries({ queryKey: ["castle"] });
    },
  });
}

// Bit (day-1) set means claimed. Mask is plain bigint number from server
// — JS Number is safe up to 53 bits, which covers all foreseeable
// pass durations.
export function isDayClaimed(mask: number, day: number): boolean {
  return (mask & (1 << (day - 1))) !== 0;
}
